import { useState } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { seoData } from '@/utils/seoData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const QuotePage = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    service_type: '',
    project_details: '',
    budget_range: '',
  });

  const content = {
    tr: {
      title: 'Teklif Alın',
      subtitle: 'Projeniz için özel teklif isteği gönderin',
      form: {
        companyName: 'Şirket Adı',
        contactPerson: 'Yetkili Kişi',
        email: 'E-posta',
        phone: 'Telefon',
        serviceType: 'Hizmet Türü',
        projectDetails: 'Proje Detayları',
        budgetRange: 'Bütçe Aralığı (Opsiyonel)',
        submit: 'Teklif İsteği Gönder',
        success: 'Teklif isteğiniz alındı! En kısa sürede dönüş yapacağız.',
        error: 'Bir hata oluştu. Lütfen tekrar deneyin.',
      },
      services: [
        'Endüstriyel Otomasyon',
        'Elektrik Pano Sistemleri',
        'Enerji Yönetimi',
        'Veri Merkezi Elektrik',
        'Senkronizasyon Panoları',
        'Diğer',
      ],
      budgets: [
        '0-50.000 TL',
        '50.000-100.000 TL',
        '100.000-250.000 TL',
        '250.000-500.000 TL',
        '500.000+ TL',
      ],
    },
    en: {
      title: 'Get a Quote',
      subtitle: 'Send a custom quote request for your project',
      form: {
        companyName: 'Company Name',
        contactPerson: 'Contact Person',
        email: 'Email',
        phone: 'Phone',
        serviceType: 'Service Type',
        projectDetails: 'Project Details',
        budgetRange: 'Budget Range (Optional)',
        submit: 'Send Quote Request',
        success: 'Your quote request has been received! We will get back to you soon.',
        error: 'An error occurred. Please try again.',
      },
      services: [
        'Industrial Automation',
        'Electrical Panel Systems',
        'Energy Management',
        'Data Center Electrical',
        'Synchronization Panels',
        'Other',
      ],
      budgets: [
        '0-50,000 TL',
        '50,000-100,000 TL',
        '100,000-250,000 TL',
        '250,000-500,000 TL',
        '500,000+ TL',
      ],
    },
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success(t.form.success);
      setFormData({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        service_type: '',
        project_details: '',
        budget_range: '',
      });
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const pageSeo = seoData.quote[language];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="quote-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="quote-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-gray-200 rounded-xl p-8" data-testid="quote-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="company_name">{t.form.companyName}</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  data-testid="quote-company"
                />
              </div>
              <div>
                <Label htmlFor="contact_person">{t.form.contactPerson}</Label>
                <Input
                  id="contact_person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                  required
                  data-testid="quote-contact"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email">{t.form.email}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="quote-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">{t.form.phone}</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="quote-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="service_type">{t.form.serviceType}</Label>
              <Select 
                value={formData.service_type} 
                onValueChange={(value) => handleSelectChange('service_type', value)}
              >
                <SelectTrigger data-testid="quote-service">
                  <SelectValue placeholder={t.form.serviceType} />
                </SelectTrigger>
                <SelectContent>
                  {t.services.map((service, index) => (
                    <SelectItem key={index} value={service}>{service}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="project_details">{t.form.projectDetails}</Label>
              <Textarea
                id="project_details"
                name="project_details"
                value={formData.project_details}
                onChange={handleChange}
                required
                rows={6}
                data-testid="quote-details"
              />
            </div>

            <div>
              <Label htmlFor="budget_range">{t.form.budgetRange}</Label>
              <Select 
                value={formData.budget_range} 
                onValueChange={(value) => handleSelectChange('budget_range', value)}
              >
                <SelectTrigger data-testid="quote-budget">
                  <SelectValue placeholder={t.form.budgetRange} />
                </SelectTrigger>
                <SelectContent>
                  {t.budgets.map((budget, index) => (
                    <SelectItem key={index} value={budget}>{budget}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              type="submit" 
              size="lg" 
              className="w-full"
              disabled={loading}
              data-testid="quote-submit"
            >
              {loading ? '...' : t.form.submit}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;